import { ReplaySubject } from 'rxjs';

import AlertDialog from '../../components/ui/dialogs/alert-dialog.component';
import { container } from '../../context/module';
import { DropboxFile } from '../../models/dropbox-file';
import { FileUploadRequestMetadata } from '../../models/file-upload-request-metadata';
import { GoogleDriveFile } from '../../models/google-drive-file';
import { Logger } from '../../models/logger';
import { UploadInProgressFile, UploadInProgressFileFactory } from '../../models/upload-in-progress-file';
import { GAService } from '../../services/analytics.service';
import { ApiConfig } from '../../services/api-config';
import { EnforcementErrorService } from '../../services/enforcement-error.service';
import { FileUploadService } from '../../services/file-upload.service';
import { LightHttp } from '../../services/light-http.service';
import { ProcessorResolverService } from '../../services/processor-resolver.service';
import { UIContext } from '../../services/ui-context';
import { FileSelectEvent, FileSelectSource, UploadOptions } from '../../types/file-upload.types';
import { GAContants } from '../../types/ga.types';
import { Observer } from 'rxjs';
import { OperationTypes, OriginalInputFile } from '../../types/types';
import { ConnectivityUtils } from '../../utils/connectivity.utils';
import { filter } from '../../utils/lodash-min';
import { safeExt, sizeBucket } from '../../utils/metric-attrs';
import { StringUtils } from '../../utils/string-utils';
import { FileValidationService, RejectedFile } from '../../services/file-validation.service';
import { FeatureRedirectService } from '../../services/feature-redirect.service';
import { MetricsService } from '../../services/metrics.service';
import { MetricEventName } from '../../types/metric-events';
import { RouteUtils } from '../../utils/route.utils';


export type SupportedFileTypes = DropboxFile | GoogleDriveFile | File;

export interface FileSelectHandleRequest<T> {
    files: T[];
    fakeUploadInProgressFileForSelectedFiles: UploadInProgressFile<SupportedFileTypes>[];
    fileWithMetadataList: UploadInProgressFile<SupportedFileTypes>[];
}

export class FileUploadManager {
    private fileUploadService: FileUploadService;
    private processorResolverService: ProcessorResolverService;
    private uiContext: UIContext;
    private gaService: GAService;

    private apiConfig: ApiConfig;
    private lightHttp: LightHttp;
    private fileValidationService: FileValidationService;
    private featureRedirectService: FeatureRedirectService;
    private metricsService: MetricsService;

    private _validationResult: ReplaySubject<{ rejected: RejectedFile[], totalFiles: number }> = new ReplaySubject(1);
    public readonly validationResult = this._validationResult.asObservable();
    private _fileListChanged: ReplaySubject<{
        fakeUploadInProgressFileForSelectedFiles: UploadInProgressFile<SupportedFileTypes>[],
        fileWithMetadataList: UploadInProgressFile<SupportedFileTypes>[]
    }> = new ReplaySubject(1);
    public readonly fileListChanged = this._fileListChanged.asObservable();

    private uploadSessionId: string;

    constructor(
        private fakeUploadInProgressFileForSelectedFiles: UploadInProgressFile<SupportedFileTypes>[],
        private fileWithMetadataList: UploadInProgressFile<SupportedFileTypes>[],
        private props: any,
        private logger: Logger,
        private fromType: string[],
        private toType: string,
        private initializeStateUpdateFn: () => void,
        private updateSidePanelFileUploads: () => void,
    ) {
        this.uploadSessionId = StringUtils.uuid();
        this.fileUploadService = container.get(FileUploadService);
        this.processorResolverService = container.get(ProcessorResolverService);
        this.uiContext = container.get(UIContext);
        this.gaService = container.get(GAService);
        this.apiConfig = container.get(ApiConfig);
        this.lightHttp = container.get(LightHttp);
        this.fileValidationService = new FileValidationService();
        this.featureRedirectService = new FeatureRedirectService(this.lightHttp, this.apiConfig);
        try { this.metricsService = container.get(MetricsService); } catch { /* optional */ }
    }

    public updateFileList(files: UploadInProgressFile<SupportedFileTypes>[]) {
        this.fileWithMetadataList = files;
    }

    public dispose() {
        this._fileListChanged.complete();
        this._validationResult.complete();
    }

    private notifyFileListChanged() {
        this._fileListChanged.next({
            fakeUploadInProgressFileForSelectedFiles: this.fakeUploadInProgressFileForSelectedFiles,
            fileWithMetadataList: this.fileWithMetadataList,
        });
    }

    async fileSelected(event: FileSelectEvent) {
        // Extract files before any early-return — browser clears event.target.files after handler
        const extractedFiles = event.files ? [...event.files] : [];
        const safeEvent: FileSelectEvent = { ...event, files: extractedFiles };

        if (!ConnectivityUtils.isOnline()) {
            this.uiContext.showErrorDialog({
                title: 'No Internet Connection',
                text: 'Please check your internet connection and try again.',
                confirmButtonText: 'Retry',
                successCallback: () => this.fileSelected(safeEvent),
            });
            return;
        }

        if (safeEvent && (safeEvent.target || safeEvent.files && safeEvent.files.length > 0)) {
            this.initializeStateUpdateFn();
        }
        this.fileUploadService.fileSelected();
        this.uiContext.successToast('Preparing To Upload', '');
        let dropboxFiles: any[] = filter(safeEvent.files, f => f instanceof DropboxFile);
        let driverFiles: any[] = filter(safeEvent.files, f => f instanceof GoogleDriveFile);
        let files: any[] = filter(safeEvent.files, f => f instanceof File);


        // Validate file extensions against page's accepted extensions
        const pageData = RouteUtils.getPageDataFromProps(this.props);
        if (pageData?.acceptedExtensions?.length > 0) {
            const allLocalFiles = [...files] as File[];
            const validation = this.fileValidationService.validate(allLocalFiles, pageData);
            if (validation.rejected.length > 0) {
                files = validation.accepted;
                const totalFiles = allLocalFiles.length;
                // Emit metrics for each rejected file
                validation.rejected.forEach(rf => {
                    this.metricsService?.emit(MetricEventName.INVALID_FILE_SELECTED, {
                        fileName: rf.file.name,
                        fileExtension: rf.extension,
                        pageSlug: pageData.path || '',
                    });
                });
                this.metricsService?.emit(MetricEventName.VALIDATION_BANNER_SHOWN, {
                    rejectedCount: validation.rejected.length,
                    acceptedCount: validation.accepted.length,
                    pageSlug: pageData.path || '',
                });
                // Async: fetch suggestions for rejected files. totalFiles is passed through so
                // later re-emits keep saying "2 of 5" rather than collapsing to "2 of 2".
                validation.rejected.forEach(r => r.suggestionStatus = 'loading');
                this.loadSuggestionsForRejectedFiles(validation.rejected, pageData, totalFiles);
                this._validationResult.next({ rejected: validation.rejected, totalFiles });
            }
            if (files.length === 0 && dropboxFiles.length === 0 && driverFiles.length === 0) {
                return; // All files rejected, nothing to upload
            }
        }

        // FILE_SELECTED — the top of the funnel, and the sibling of the
        // INVALID_FILE_SELECTED emit above. Until now only the REJECTED half of
        // this decision was recorded, so "how many people got a usable file as
        // far as the uploader" had no denominator and CONVERT_START's 4.7k/day
        // could not be compared against anything upstream of it.
        // Emitted once per accepted file, after extension validation and before
        // the size-limit filter (a file too large for the plan was still
        // selected — that loss is measured by its absence downstream, not by
        // pretending the pick never happened).
        this.emitFileSelected(
            [...files, ...dropboxFiles, ...driverFiles],
            safeEvent.source,
            pageData?.path || '',
        );
        // @Deprecated
        // if (this.offlineTaskService.getOfflineMode()) {
        //     try {
        //         const capability = this.capabilityService.getConverters().find(c => c.id == RouteUtils.getPathFromProps(this.props));
        //         const canProcessOffline = capability.availability.offline;
        //         if (!canProcessOffline) {
        //             this.uiContext.showErrorDialog(
        //                 { title: 'Error', text: `Offline mode is not supported for this feature. Please turn it off and reload the page.` });
        //         }
        //     } catch (e) {
        //         console.error(e);
        //     }
        // }
        // console.log(dropboxFiles, driverFiles, files);

        const failToUploadFileNames = [];

        if (driverFiles.find(selectedFile => selectedFile.size > this.fileUploadService.maxFileSizeInBytes)) {
            failToUploadFileNames.push(driverFiles.filter(selectedFile => selectedFile.size > this.fileUploadService.maxFileSizeInBytes).map(selectedFile => selectedFile.name));
            driverFiles = driverFiles.filter(selectedFile => selectedFile.size <= this.fileUploadService.maxFileSizeInBytes);
        }

        if (dropboxFiles.find(selectedFile => selectedFile.size > this.fileUploadService.maxFileSizeInBytes)) {
            failToUploadFileNames.push(dropboxFiles.filter(selectedFile => selectedFile.size > this.fileUploadService.maxFileSizeInBytes).map(selectedFile => selectedFile.name));
            dropboxFiles = dropboxFiles.filter(selectedFile => selectedFile.size <= this.fileUploadService.maxFileSizeInBytes);
        }

        if (files.find(selectedFile => selectedFile.size > this.fileUploadService.maxFileSizeInBytes)) {
            failToUploadFileNames.push(files.filter(selectedFile => selectedFile.size > this.fileUploadService.maxFileSizeInBytes).map(selectedFile => selectedFile.name));
            files = files.filter(selectedFile => selectedFile.size <= this.fileUploadService.maxFileSizeInBytes);
        }

        if (failToUploadFileNames.length > 0) {
            this.logger.info({ message: `File Size Limit Exceeded! Failed to upload ${failToUploadFileNames.join(', ')}. Max file size limit is ${this.fileUploadService.maxFileSizeInMB} MB.` });
            AlertDialog.create({
                title: 'File Size Limit Exceeded!',
                html: `<div style="text-align: left;">Failed to upload <ul><li>${failToUploadFileNames.join('</li><li>')}</li></ul>
              Max file size limit is ${this.fileUploadService.maxFileSizeInMB} MB.</div>`,
                type: 'danger',
                showCancelButton: false,
                confirmButtonText: 'Ok!',
            });
        }

        // file.size dropbox.size
        try {
            /// TODO loadFeaturePageDataForProps Note tested in Peact
            const uniqueProcessType = RouteUtils.getPageDataFromProps(this.props).uniqueProcessType;
            for (const file of [...dropboxFiles, ...driverFiles, ...files]) {
                const fakeUploadInProgress = await UploadInProgressFileFactory.newUploadInProgressFile(
                    file,
                    new FileUploadRequestMetadata([], file),
                    uniqueProcessType,
                );
                this.fakeUploadInProgressFileForSelectedFiles.push(fakeUploadInProgress);
                this.notifyFileListChanged();
            }


            await this.dropboxFileSelected({
                files: dropboxFiles,
                fileWithMetadataList: this.fileWithMetadataList,
                fakeUploadInProgressFileForSelectedFiles: this.fakeUploadInProgressFileForSelectedFiles
            });
            await this.googleDriveFileSelected({
                files: driverFiles,
                fileWithMetadataList: this.fileWithMetadataList,
                fakeUploadInProgressFileForSelectedFiles: this.fakeUploadInProgressFileForSelectedFiles
            });
            await this.multiFileUploadEvent({
                files: files,
                fileWithMetadataList: this.fileWithMetadataList,
                fakeUploadInProgressFileForSelectedFiles: this.fakeUploadInProgressFileForSelectedFiles
            });

            Promise.all(this.fileWithMetadataList.map(fml => fml.whenComplete())).then(
                () => {
                    this.updateSidePanelFileUploads();
                    this.notifyFileListChanged();
                },
            ).catch(console.error);

            // Stuck file watchdog — detect files at 0% with no error after 60s
            setTimeout(() => {
                this.fileWithMetadataList.forEach(f => {
                    if (!f.completed && !f.error && f.progress === 0 && ConnectivityUtils.isOnline()) {
                        this.logger.error({
                            message: 'upload.file.stuck',
                            data: {
                                uploadSessionId: this.uploadSessionId,
                                fileName: (f.file as any)?.name,
                                hasMetadata: !!f.metadata?.processorMetadataList?.length,
                            },
                        });
                    }
                });
            }, 60000);
        } catch (e) {
            this.logger.error({ error: e, message: 'File upload failed.', data: { uploadSessionId: this.uploadSessionId } });
            if (ConnectivityUtils.isConnectivityError(e)) {
                this.uiContext.showErrorDialog({
                    title: 'Server Unreachable',
                    text: 'The upload server is temporarily unavailable. Please try again.',
                    confirmButtonText: 'Retry',
                    successCallback: () => this.fileSelected(safeEvent),
                });
            } else {
                this.uiContext.handleError(e);
            }
        }
    }

    /**
     * One FILE_SELECTED per accepted file, with bounded attributes only.
     *
     * `source` is taken from the event when a call site asserted it (hand-off,
     * session tray) and otherwise derived from the file object: a DropboxFile is
     * fetched by the edge from a URL, a GoogleDriveFile through the Drive API,
     * anything else is bytes off the device.
     */
    private emitFileSelected(accepted: any[], eventSource: FileSelectSource | undefined, pageSlug: string) {
        for (const file of accepted) {
            try {
                const source: FileSelectSource = eventSource
                    || (file instanceof GoogleDriveFile ? 'drive'
                        : file instanceof DropboxFile ? 'url'
                            : 'local');
                this.metricsService?.emit(MetricEventName.FILE_SELECTED, {
                    pageSlug,
                    fileExtension: safeExt(file?.name || ''),
                    sizeBucket: sizeBucket(file?.size),
                    source,
                });
            } catch { /* telemetry is never load-bearing */ }
        }
    }

    async dropboxFileSelected(request: FileSelectHandleRequest<DropboxFile>): Promise<any> {
        if (request.files.length > 0) {
            const uniqueProcessType = RouteUtils.getPageDataFromProps(this.props).uniqueProcessType;
            const currentRequestMetadata: UploadInProgressFile<DropboxFile>[] = [];
            for (const file of request.files) {
                currentRequestMetadata.push(await UploadInProgressFileFactory.newUploadInProgressFile(
                    file, null, uniqueProcessType,
                ));
            }
            request.fileWithMetadataList.push(...currentRequestMetadata);
            this.notifyFileListChanged();

            try {
                for (const currentRequest of currentRequestMetadata) {
                    this.gaService.send(
                        GAContants.HitType.EVENT, GAContants.EventCategory['SELECT-DIALOG'],
                        GAContants.EventAction['FILE-SELECTED'], GAContants.EventLabel.SELECT,
                        `drive-${currentRequest.file.name}-${currentRequest.file.size}`,
                    );
                    try {
                        const metadata = await this.resolveWithRetry(currentRequest.file);
                        currentRequest.setMetadata(metadata);
                    } catch (e) {
                        this.logger.error({ message: `Resolve failed for ${currentRequest.file.name}`, error: e, data: { uploadSessionId: this.uploadSessionId } });
                        currentRequest.setError(() => this.retryResolveAndUpload(
                            currentRequest, (fwm, obs, opts) => this.fileUploadService.uploadFileFromURL(fwm, obs, opts)));
                    }
                }
                this.notifyFileListChanged();

                const resolvedFiles = currentRequestMetadata.filter(f => f.metadata?.processorMetadataList?.length > 0);
                const promiseArray = [];
                for (const fileWithMetadata of resolvedFiles) {
                    promiseArray.push(new Promise<void>((resolve, reject) => {
                        const observerObject = {
                            next: x => { fileWithMetadata.setProgress(x); },
                            error: err => {
                                this.logger.error({ message: `Error uploading file (Dropbox).`, error: err, data: { uploadSessionId: this.uploadSessionId } });
                                fileWithMetadata.setError();
                            },
                            complete: () => { fileWithMetadata.setComplete(); },
                        };
                        this.uploadWithRetry(
                            fileWithMetadata,
                            (fwm, obs, opts) => this.fileUploadService.uploadFileFromURL(fwm, obs, opts),
                            observerObject,
                        ).then(() => resolve()).catch((e) => reject(e));
                    }));
                }
                await Promise.all(promiseArray);
            } finally {
                request.files.forEach(f => {
                    const idx = request.fakeUploadInProgressFileForSelectedFiles.findIndex(fakeF => fakeF.file === f);
                    if (idx > -1) {
                        request.fakeUploadInProgressFileForSelectedFiles.splice(idx, 1);
                    }
                });
                this.notifyFileListChanged();
            }
        }
    }

    async googleDriveFileSelected(request: FileSelectHandleRequest<GoogleDriveFile>) {
        if (request.files.length > 0) {
            const uniqueProcessType = RouteUtils.getPageDataFromProps(this.props).uniqueProcessType;
            this.logger.trace7({ message: 'Goolge drive file selected.', data: request.files });
            const currentRequestMetadata: UploadInProgressFile<any>[] = [];

            for (const file of request.files) {
                currentRequestMetadata.push(await UploadInProgressFileFactory.newUploadInProgressFile(
                    file, null, uniqueProcessType,
                ));
            }
            request.fileWithMetadataList.push(...currentRequestMetadata);
            this.notifyFileListChanged();

            try {
                for (const currentRequest of currentRequestMetadata) {
                    this.gaService.send(
                        GAContants.HitType.EVENT, GAContants.EventCategory['SELECT-DIALOG'],
                        GAContants.EventAction['FILE-SELECTED'], GAContants.EventLabel.SELECT,
                        `drive-${currentRequest.file.name}-${currentRequest.file.size}`,
                    );
                    try {
                        const metadata = await this.resolveWithRetry(currentRequest.file);
                        currentRequest.setMetadata(metadata);
                    } catch (e) {
                        this.logger.error({ message: `Resolve failed for ${currentRequest.file.name}`, error: e, data: { uploadSessionId: this.uploadSessionId } });
                        currentRequest.setError(() => this.retryResolveAndUpload(
                            currentRequest, (fwm, obs, opts) => this.fileUploadService.uploadFileFromGoogleDrive(fwm as UploadInProgressFile<File>, obs, opts)));
                    }
                }
                this.notifyFileListChanged();

                const resolvedFiles = currentRequestMetadata.filter(f => f.metadata?.processorMetadataList?.length > 0);
                const promiseArray = [];
                for (const fileWithMetadata of resolvedFiles) {
                    promiseArray.push(new Promise<void>((resolve, reject) => {
                        const observerObject = {
                            next: x => { fileWithMetadata.setProgress(x); },
                            error: err => {
                                this.logger.error({ message: `Error uploading file (GoogleDrive).`, error: err, data: { uploadSessionId: this.uploadSessionId } });
                                fileWithMetadata.setError();
                            },
                            complete: () => { fileWithMetadata.setComplete(); },
                        };
                        this.uploadWithRetry(
                            fileWithMetadata,
                            (fwm, obs, opts) => this.fileUploadService.uploadFileFromGoogleDrive(fwm as UploadInProgressFile<File>, obs, opts),
                            observerObject,
                        ).then(() => resolve()).catch((e) => reject(e));
                    }));
                }
                await Promise.all(promiseArray);
            } finally {
                request.files.forEach(f => {
                    const idx = request.fakeUploadInProgressFileForSelectedFiles.findIndex(fakeF => fakeF.file === f);
                    if (idx > -1) {
                        request.fakeUploadInProgressFileForSelectedFiles.splice(idx, 1);
                    }
                });
                this.notifyFileListChanged();
            }
        }
    }

    async multiFileUploadEvent(request: FileSelectHandleRequest<File>) {
        const uniqueProcessType = RouteUtils.getPageDataFromProps(this.props).uniqueProcessType;

        const currentRequestMetadata: UploadInProgressFile<any>[] = [];
        for (const file of request.files) {
            currentRequestMetadata.push(await UploadInProgressFileFactory.newUploadInProgressFile(
                file,
                null,
                uniqueProcessType,
            ));
        }
        request.fileWithMetadataList.push(...currentRequestMetadata);
        this.notifyFileListChanged();

        try {
            for (const currentRequest of currentRequestMetadata) {
                this.gaService.send(
                    GAContants.HitType.EVENT,
                    GAContants.EventCategory['SELECT-DIALOG'],
                    GAContants.EventAction['FILE-SELECTED'],
                    GAContants.EventLabel.SELECT,
                    `file-${currentRequest.file.name}-${currentRequest.file.size}`,
                );
                try {
                    const metadata = await this.resolveWithRetry(currentRequest.file);
                    currentRequest.setMetadata(metadata);
                } catch (e) {
                    this.logger.error({ message: `Resolve failed for ${currentRequest.file.name}`, error: e, data: { uploadSessionId: this.uploadSessionId } });
                    currentRequest.setError(() => this.retryResolveAndUpload(
                        currentRequest, (fwm, obs, opts) => this.fileUploadService.uploadFileFromLocalDisk(fwm as UploadInProgressFile<File>, obs, opts)));
                }
            }

            const resolvedFiles = currentRequestMetadata.filter(f => f.metadata?.processorMetadataList?.length > 0);
            const CONCURRENT_LIMIT = 4;
            for (let i = 0; i < resolvedFiles.length; i += CONCURRENT_LIMIT) {
                const batch = resolvedFiles.slice(i, i + CONCURRENT_LIMIT);
                await Promise.all(batch.map(fileWithMetadata => new Promise<void>((resolve, reject) => {
                    const observerObject = {
                        next: x => { fileWithMetadata.setProgress(x); },
                        error: err => {
                            this.logger.error({ message: `Error uploading file (Disk_).`, error: err, data: { uploadSessionId: this.uploadSessionId } });
                            fileWithMetadata.setError();
                        },
                        complete: () => { fileWithMetadata.setComplete(); },
                    };
                    this.uploadWithRetry(
                        fileWithMetadata,
                        (fwm, obs, opts) => this.fileUploadService.uploadFileFromLocalDisk(fwm as UploadInProgressFile<File>, obs, opts),
                        observerObject,
                    ).then(() => resolve()).catch((e) => reject(e));
                })));
            }
        } finally {
            request.files.forEach(f => {
                const idx = request.fakeUploadInProgressFileForSelectedFiles.findIndex(fakeF => fakeF.file === f);
                if (idx > -1) {
                    request.fakeUploadInProgressFileForSelectedFiles.splice(idx, 1);
                }
            });
            this.notifyFileListChanged();
        }
    }

    private async resolveWithRetry(file: any, maxRetries = 1): Promise<FileUploadRequestMetadata> {
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                return await this.processorResolverService.generateFileUploadRequestMetadata(
                    file, this.fromType, this.toType, OperationTypes.UPLOAD,
                    attempt > 0 ? { suppressGA: true } : undefined);
            } catch (e) {
                // Enforcement / billing rejections (429 enforcement_error,
                // 402 insufficient_credits) have already surfaced via the
                // global EnforcementErrorModal inside the resolver. Re-trying
                // would just retrigger the same rejection — bail out.
                if (EnforcementErrorService.isEnforcementError(e) || attempt === maxRetries) throw e;
                this.logger.warn({ message: `Resolve failed (attempt ${attempt + 1}), retrying.`, error: e, data: { uploadSessionId: this.uploadSessionId } });
            }
        }
        throw new Error('Resolve failed');
    }

    private async retryResolveAndUpload(
        fileWithMetadata: UploadInProgressFile<SupportedFileTypes>,
        uploadFn: (fwm: UploadInProgressFile<SupportedFileTypes>, obs: Observer<any>, opts?: UploadOptions) => Promise<OriginalInputFile>,
    ): Promise<void> {
        fileWithMetadata.error = false;
        fileWithMetadata.setProgress(0);
        const metadata = await this.processorResolverService.generateFileUploadRequestMetadata(
            fileWithMetadata.file as any, this.fromType, this.toType, OperationTypes.UPLOAD);
        fileWithMetadata.setMetadata(metadata);
        const observerObject = {
            next: x => { fileWithMetadata.setProgress(x); },
            error: err => { fileWithMetadata.setError(); },
            complete: () => { fileWithMetadata.setComplete(); },
        };
        await this.uploadWithRetry(fileWithMetadata, uploadFn, observerObject);
    }


    private async uploadWithRetry(
        fileWithMetadata: UploadInProgressFile<SupportedFileTypes>,
        uploadFn: (fwm: UploadInProgressFile<SupportedFileTypes>, obs: Observer<any>, opts?: UploadOptions) => Promise<OriginalInputFile>,
        baseObserverObject: Observer<any>,
        maxRetries = 2,
    ): Promise<void> {
        const excludeDomains: string[] = [];
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            const isFinalAttempt = attempt === maxRetries;
            const isFirstAttempt = attempt === 0;
            const observerObject = {
                ...baseObserverObject,
                error: (err) => { if (isFinalAttempt) baseObserverObject.error(err); },
            };
            try {
                const result = await uploadFn(fileWithMetadata, observerObject, {
                    suppressErrorDialog: !isFinalAttempt,
                    suppressGAStart: !isFirstAttempt,
                    suppressGAFailed: !isFinalAttempt,
                    // Surfaced as FILE_UPLOAD_*.retries so the edge-reselection
                    // loop stops being invisible.
                    attempt,
                });
                fileWithMetadata.setResponse(result);
                return;
            } catch (e) {
                if (!ConnectivityUtils.isRetryableUploadError(e) || isFinalAttempt) {
                    fileWithMetadata.setError(() => this.uploadWithRetry(
                        fileWithMetadata, uploadFn, baseObserverObject, maxRetries));
                    throw e;
                }
                const failedDomain = fileWithMetadata.metadata?.processorMetadataList?.[0]?.uploadDomain;
                if (failedDomain) excludeDomains.push(failedDomain);
                this.logger.warn({ message: `Upload failed (attempt ${attempt + 1}), re-resolving edge. Excluding: ${excludeDomains.join(', ')}`, error: e, data: { uploadSessionId: this.uploadSessionId } });
                fileWithMetadata.error = false;
                fileWithMetadata.setProgress(0);
                const newMetadata = await this.processorResolverService
                    .generateFileUploadRequestMetadata(
                        fileWithMetadata.file as any, this.fromType, this.toType,
                        OperationTypes.UPLOAD, { suppressGA: true, excludeDomains });
                fileWithMetadata.setMetadata(newMetadata);
            }
        }
    }

    /**
     * @param totalFiles the size of the batch the user actually selected. It is threaded through
     * rather than recomputed, because `rejected.length` is a different number: re-emitting with
     * it made the headline mutate from "2 of 5 files were not uploaded" to "2 of 2" the moment
     * suggestions landed, which tells the user their other three files vanished too.
     */
    private async loadSuggestionsForRejectedFiles(rejected: RejectedFile[], pageData: any, totalFiles: number) {
        const uniqueExts = [...new Set(rejected.map(r => r.extension))];
        for (const ext of uniqueExts) {
            const forExt = rejected.filter(r => r.extension === ext);
            try {
                const suggestions = await this.featureRedirectService.findRelevantPages(ext, pageData);
                forExt.forEach(r => {
                    r.suggestedPages = suggestions;
                    r.suggestionStatus = 'loaded';
                });
            } catch {
                // Best-effort, but the failure has to be visible: an unmarked failure leaves the
                // row saying "Finding the right tool..." for the rest of the session.
                forExt.forEach(r => r.suggestionStatus = 'failed');
            }
            // Emit a new array reference so React detects the change
            this._validationResult.next({ rejected: [...rejected], totalFiles });
        }
    }
}
